import $ from 'jquery'
import Backbone from 'backbone'
import _ from 'lodash'
import Tooltip from 'scoreboard/components/tooltip'
import ConnectorConfig from './connector-config'
import template from './widget-control-view.hbs'
import togglePrefixedClass from 'scoreboard/components/toggle-prefixed-class'
import { SHAPE_TYPE } from './shape/shape-type'
const { RECTANGLE, ELLIPSE, RIGHT_TRIANGLE, DIAMOND, PENTAGON, CLOUD,
	SEAL } = SHAPE_TYPE

function defaultConnectorStyle () {
	return {
		opacity: .5,
		'stroke-width': 4
	}
}

// Constants for #24535 to control the config button location based on wob size
const MIN_INSIDE_WIDTH = 54
const MIN_INSIDE_HEIGHT = 52

var WidgetControlView = Backbone.View.extend({
	// model is the widget. It has reference to `.wob` if needed.

	className: 'widget-control-view',

	events: {
		'mousedown .config-handle-settings': 'noop',
		'mousedown .config-handle-connector': 'noop',
		'click .config-handle-settings': 'config',
		'click .config-handle-connector': 'addConnector',
		'touchstart .config-handle-settings': 'config',
		'touchmove .config-handle-settings': 'nativeTouchConfig',
		'touchstart .config-handle-connector': 'addConnector',
		'touchmove .config-handle-connector': 'nativeTouchAddConnector'
	},

	initialize: function (opt) {
		this.container = opt.container
		this.canvas = opt.canvas
		this.widgetView = opt.widgetView

		_.bindAll(this, 'focus', 'config', '_onResize', 'removeConfig', '_endResize', '_startResize', '_adjustConfigLocation')

		this.listenTo(this.model.wob, 'connectorsConfigurable', this.connectorConfig)

		// #17924 after resizing through the config menu, run the same "adjust the wob to fit the actual contents" logic
		// that we do on mouseup after the user resizes with the mouse
		this.listenTo(this.model.wob, 'config:resize', this._endResize)
		this.listenTo(this.model.wob, 'change:rotate', this._adjustConfigLocation)

		this.container
			.on('selected', this.focus)
			.on('resized', this._onResize)
			.on('moved', this.removeConfig)
			.on('start:resize', this._startResize)
			.on('end:resize', this._endResize)
	},

	noop: function (e) {
		// Prevent events from bubbling. This is needed so draggable code (and other canvas event handlers)
		// when someone clicks the config buttons
		e.preventDefault()
		e.stopPropagation()
	},

	render: function () {
		this.$el.append(template({connectable: this.model.connectable}))

		togglePrefixedClass(this.$el.closest('.wob-control'), 'background', this.model.properties.get('background') || 'none')

		this.togglePositionOutside()

		this._adjustConfigLocation()

		this.$el.parent().on('start:rotate', () => this.startRotate())

		return this
	},

	focus: function (e, on) {
		var allowConfig = on && this.widgetView.allowConfig()

		this.$('.config-handle-settings').toggleClass('is-allowed-config', allowConfig)
		this.$('.config-handle-connector').toggleClass('is-allowed-config', allowConfig)

		!allowConfig && this.removeConfig()
	},

	/**
	 * On real touch devices (not emulators) the touch event may come through as
	 * a move event. We want to keep this move event from going any further,
	 * which would close the pop-up menu. We also wish to prevent multiple move
	 * events fired as part of the same touch from opening and closing the menu.
	 */
	nativeTouchConfig: function(e) {
		e.stopPropagation()
		_.debounce(this.config, 100)
	},

	// See comments for nativeTouchConfig
	nativeTouchAddConnector: function (e) {
		e.stopPropagation()
		_.debounce(this.addConnector, 100)
	},

	addConnector: function (e) {
		this.canvas.history.record()
		// Add a placeholder connector to this widget's wob
		this.canvas.deselectAll()
		this.model.wob.get('connectors').add({
			selected: true,
			style: defaultConnectorStyle()
		})
	},

	connectorConfig: function (connector, configurable, intersectionPoint, intersectionOffset) {
		if (!configurable) {
			// The connector cannot currently be configured, so make sure we're now showing the tooltip
			this.removeConnectorConfig()
			// Our job is done here
			return
		}

		if (this.connectorTooltip) {
			// We have a tooltip, clean it up and we'll show a new one
			this.removeConnectorConfig()
		}

		this.connectorTooltip = new Tooltip({
			attachTo: intersectionPoint,
			attachToCenter: true, // defaults to true, indicates if arrow should point to the center of attachTo
			content: new ConnectorConfig({model: connector, canvas: this.canvas}),
			placement: intersectionOffset > 0 ? 'below' : 'above', // if the offset is positive, it's above the `path` center, so put the tooltip below
			arrow: 'center',
			adjustment: 1,
			extraClass: 'dashboard-connector-tooltip',
			closeOnChangedFocus: false // false if clicking outside the tooltips shouldn't close it (defaults to true)
		}).render()
	},

	removeConnectorConfig: function () {
		if (this.connectorTooltip) {
			this.connectorTooltip.remove()
			delete this.connectorTooltip
		}
	},

	config: function (e) {
		// Needed for when we click on the config button in order to prevent
		// the menu from going away in mobile
		// https://github.com/SpiderStrategies/Scoreboard/issues/17378
		e.preventDefault()

		if(this.tooltip && !this.tooltip.removed) {
			// subsequent touches on the config button should close the menu
			this.removeConfig()
		}
		else {
			if(this.tooltip) {
				// The tooltip is closed but exists so we should clean it up
				// before opening a new one.
				this.removeConfig()
			}
			this.tooltipContent = this._createConfigView()

			let configHandle = this.$('.config-handle-settings')

			this.tooltip = new Tooltip({
				attachTo: configHandle,
				content: this.tooltipContent,
				placement: 'right',
				arrow: 'top',
				extraClass: 'widget-config has-bootslide-menu',
				keepOpenOnScroll: () => keepOpenOnScroll(configHandle)
			}).render()
		}
	},

	_createConfigView: function () {
		if (this.widgetView._createConfigView) {
			return this.widgetView._createConfigView.apply(this.widgetView, arguments)
		}
		this.widgetView.trigger('config')
		return new this.widgetView.configClass({
			model: this.model,
			canvas: this.canvas,
			widgetUpdater: this.widgetView.widgetUpdater
		})
	},

	togglePositionOutside: function (dimensions) {
		if (!dimensions) {
			dimensions = this.model.wob.pick('width', 'height')
		}

		// If the wob is too small, we want to style the config button outside the wob
		let positionOutside = dimensions.width < MIN_INSIDE_WIDTH || dimensions.height < MIN_INSIDE_HEIGHT

		if (this.model.connectable && !positionOutside) {
			// If this wob is connectable, there's two buttons side-by-side
			// because of the connectors config, so we need more space
			positionOutside = dimensions.width < (MIN_INSIDE_WIDTH * 2)
		}

		// #57288 for shapes with drag controls, move the gear box icon outside
		// of the widget so that it doesn't cover the controls
		if (isShapeWithDragControls(this.model)) positionOutside = true

		this.$el.toggleClass('is-positioned-outside', positionOutside)

		if (positionOutside) {
			// If the config button is on the outside, it defaults to the right
			// of the widget. However, if it's too close to the end of the window,
			// we need to flip it over to the left.
			//
			// First, let canvas finish all its positioning...
			this.canvas.onPaint(() => {
				let windowWidth = $(window).width()
				let widgetBounds = this.el.getBoundingClientRect()
				// This is the 38px 'right' property on .config-handle-settings
				// plus 9px for padding on .wob-control
				let icon = 38 + 9

				let moveLeft = (widgetBounds.x + widgetBounds.width + icon) > windowWidth
				this.$el.toggleClass('is-positioned-outside-left', moveLeft)
			})
		}
	},

	_adjustConfigLocation: function () {
		let angle = this.model.wob.get('rotate')
		let wobHeight = this.model.wob.get('height')

		this.$('.config-handle-settings').css(getConfigLocationAdjustments({
			angle,
			wobHeight,
			handleType: 'settings'
		}))

		this.$('.config-handle-connector').css(getConfigLocationAdjustments({
			angle,
			wobHeight,
			handleType: 'connector'
		}))
	},

	_onResize: function (e, dimensions) {
		this.togglePositionOutside(dimensions)
		this._adjustConfigLocation()

		if (this.widgetView._onResize) {
			return this.widgetView._onResize.apply(this.widgetView, arguments)
		}

		// issue #6737 "top" will sometimes be undefined instead of 0,
		// so just guard for that
		var now = {
			width: dimensions.width,
			top: dimensions.top || 0
		}

		var previous
		if (dimensions.previous) {
			previous = {
				width: dimensions.previous.width,
				top: dimensions.previous.top || 0
			}
		} else {
			previous = { width: 0, top: 0 }
		}

		// We only need to reposition the config if the width or top position changed.
		// We aren't worried about the height, which may change as properties are
		// changed without moving the config handle
		if (now.width != previous.width || now.top != previous.top) {
			this.canvas.onPaint(() => {
				// Need to wait until the DOM node is updated.
				this.tooltip && this.tooltip.reposition()
			})
		}
	},

	/**
	 * Fired when a widget is being resized using the handles (not in the config menu)
	 */
	_startResize: function () {
		this.widgetView._resizing = true

		// #23314 no need to leave the menu open when resizing with the wob handles.
		// Note that this won't affect changing size via the config menu (explicitly
		// with the Size menu or implicitly by toggling Showing options)
		this.removeConfig()
	},

	_endResize: function() {
		// #23038 #23037 some performance-related changes were made to canvas that
		// result in wob model resize events being fired before the actual DOM node
		// is adjusted. Some widgets (like bubbles and dataset-value) need the wob's
		// DOM node to be sized properly before making their auto-adjustment calculations.
		//
		// Wait until canvas has painted its wob styles
		this.widgetView._resizing = false
		this.canvas.onPaint(() => {
			if (this.widgetView.autoResize) {
				this.container
					.off('resized', this._onResize)
					.off('end:resize', this._endResize)

				this.widgetView.adjustSize()

				this.container
					.on('resized', this._onResize)
					.on('end:resize', this._endResize)
			}
		})
	},

	startRotate() {
		// #23314 no need to leave the menu open when rotating.
		this.removeConfig()
	},

	removeConfig: function () {
		this.tooltip && this.tooltip.remove()
		delete this.tooltip

		this.tooltipContent && this.tooltipContent.remove()
		delete this.tooltipContent
	},

	remove: function () {
		this.widgetView._resizing = false
		this.removeConfig()

		Backbone.View.prototype.remove.apply(this, arguments)
	}
})

/**
 * Returns whether the config menu should remain open after a scroll, given the
 * scroll position of the configHandle
 *
 * @param {jquery} configHandle
 *
 * @return {boolean}
 */
function keepOpenOnScroll (configHandle) {
	let scrollContainer = configHandle.closest('.canvas-container')

	return dimensionIsVisible(scrollContainer, configHandle, 'top')
		&& dimensionIsVisible(scrollContainer, configHandle, 'left')
}

/**
 * Returns whether a given dimension of the config handle is visible within the
 * scroll container
 *
 * @param {jquery} scrollContainer
 * @param {jquery} configHandle
 * @param {'top'|'left'} dimension
 *
 * @return {boolean}
 */
function dimensionIsVisible (scrollContainer, configHandle, dimension) {
	let offsetProp = dimension === 'top' ? 'offsetTop' : 'offsetLeft'
	let scrollProp = dimension === 'top' ? 'scrollTop' : 'scrollLeft'
	let sizeProp = dimension === 'top' ? 'offsetHeight' : 'offsetWidth'

	// Find the "total" offsetTop/Left of the config icon relative to the
	// scroll-container for the dashboard; we can't just look at
	// configHandle.offsetTop/Left, because that is only relative to the
	// nearest positioned element (.wob-control in this case).
	//
	// Instead, we need to add up the offsetTop/Left relative to each
	// positioned element until we get to the scroll-container
	function totalOffset (el) {
		if (el.offsetParent && scrollContainer.has(el).length > 0) {
			let offset = el[offsetProp]
			return offset + totalOffset(el.offsetParent)
		}

		return 0
	}

	let totalHandleOffset = totalOffset(configHandle[0])
	let containerScroll = scrollContainer[0][scrollProp]
	let containerSize = scrollContainer[0][sizeProp]

	// If the config handle is still visible in the dashboard's
	// scroll-container, keep the tooltip open. Otherwise, the
	// tooltip should be closed
	return totalHandleOffset > containerScroll
		&& totalHandleOffset < (containerScroll + containerSize)
}

/**
 * returns true if the widget is a shape model and the shape has drag controls
 * that a user can use to adjust the size of the shape
 */
function isShapeWithDragControls(model) {
	const isShape = model.properties.get('type') === 'shape'
	const shapeType = model.properties.get('shape')
	const shapesWithoutControls = [RECTANGLE, ELLIPSE, RIGHT_TRIANGLE,
		DIAMOND, PENTAGON, CLOUD, SEAL]
	return isShape && !_.isNil(shapeType) &&
		!_.includes(shapesWithoutControls, shapeType)
}

const CONFIG_BUTTON_PADDING = 20

function getConfigLocationAdjustments ({ angle, wobHeight, handleType }) {
	let adjustments = {
		// Change the layout of the button so it's never rotated
		transform: angle === 0 ? '' : `rotate(-${angle}deg)`
	}

	// Position the handle so it's in the right location
	if (angle >= 135 && angle < 315) {
		// 180 and 270 deg rotation
		adjustments.top = wobHeight - CONFIG_BUTTON_PADDING
	} else {
		// Remove element overrides, use the default styling
		adjustments.top = ''
	}

	// The "settings" icon sometimes needs the the `right` positioning, but
	// the "connector" icon never does
	if (handleType === 'settings') {
		adjustments.right = angle >= 45 && angle < 225
			? 'auto'
			: ''
	}

	return adjustments
}

export default WidgetControlView
export { isShapeWithDragControls, getConfigLocationAdjustments, keepOpenOnScroll }

WidgetControlView.__filename__ = 'dashboards/widgets/widget-control-view.js'
